import type { CSSObject, Theme } from "@mui/material/styles";
import { PANE_STRETCH_ATTRIBUTE } from "_shared/_views/tables/table-pane-stretch";

/**
 * A work page lays itself out against the pane it actually occupies, not against the viewport.
 *
 * Two things take width away from that pane and neither is visible to a media query. The application's
 * navigation drawer is permanent from Material's `lg` up and takes 17rem of the window; the page's own
 * right-hand drawer takes another 400px whenever one is open — a proposal's comments, its two
 * fee-schedule-category panels and its vendor-team selection, a work agreement's chat. A viewport at
 * 1280px CSS pixels therefore leaves the page 1008px with every drawer closed and 608px with one open,
 * while `theme.breakpoints.down("md")` insists the page is comfortable in all three cases.
 *
 * That gap is what the display-scaling reports are about (#4964). Raising the operating system's
 * scaling does not change the device; it divides the window's CSS pixels by the scale factor, so a
 * 1920px monitor at 150% hands the browser a 1280px viewport — above every breakpoint these pages test
 * for, and 272px narrower than the pane arithmetic behind the desktop layout assumed.
 *
 * So the pane names itself as a container and everything inside measures against that. The thresholds
 * stay Material's own breakpoint values, because they are the widths the rest of the application is
 * designed around; only the thing being measured changes.
 */
export const WORK_PANE_CONTAINER = "work-pane";

/**
 * Applied to the element that owns the pane — `ProposalPageMain`, or the work agreement page's `Main`
 * — so the queries below have something to resolve against. Named rather than anonymous: the tabs nest
 * containers of their own (the team profile dialog, the document selector), and an anonymous query
 * would bind to the nearest of those.
 */
export const workPaneContainment: CSSObject = {
  containerName: WORK_PANE_CONTAINER,
  containerType: "inline-size",
};

export function workPaneAtLeast(width: number): string {
  return `@container ${WORK_PANE_CONTAINER} (min-width: ${width}px)`;
}

export function workPaneNarrowerThan(width: number): string {
  // Container query ranges are inclusive at both ends, so `atLeast(w)` and `narrowerThan(w)` would
  // both match at exactly `w` and the winner would be decided by source order rather than by intent.
  return `@container ${WORK_PANE_CONTAINER} (max-width: ${width - 0.05}px)`;
}

/**
 * Below this the pane stops being able to seat a row of full-width actions, a title and its breadcrumb,
 * and the tab strip at their desktop sizes. It is Material's `md`, which is the width the page's
 * previous viewport queries already used — read against the pane, it now also catches an open drawer
 * and a scaled-up display, which is the whole point.
 */
export function compactPaneWidth(theme: Theme): number {
  return theme.breakpoints.values.md;
}

/** `theme.spacing` yields a CSS length; container-query thresholds have to add up as numbers. */
export function spacingInPixels(theme: Theme, units: number): number {
  return parseFloat(theme.spacing(units));
}

export function paneIsCompact(theme: Theme): string {
  return workPaneNarrowerThan(compactPaneWidth(theme));
}

export function paneIsComfortable(theme: Theme): string {
  return workPaneAtLeast(compactPaneWidth(theme));
}

/**
 * The two bands a work page is drawn in, and the only thing that differs between them.
 *
 * `header` is the page talking about itself — its title, its tab strip, its row of actions. Those
 * rows stand 16px off the pane's edges on a comfortable pane as on a compact one. They stood 32px off
 * once, to frame the page, but the tab underneath runs flush to the pane, so the title, the tabs and
 * the actions row sat visibly further in than the template panel and table they introduce, and the
 * header read as padding rather than as a frame. `content` is everything below them: a tab is a
 * panel beside a table, each already carrying padding of its own, and spending more of the pane on
 * margins there comes straight out of a template panel that opens at its 375px minimum.
 *
 * Three steps rather than two, in both bands: a phone cannot spare 24px twice over, and a 390px pane
 * spending 12% of itself on margins is how the compact rules came to zero themselves out in the first
 * place. The two narrow steps are shared, because a pane that small has no width to spend on saying
 * which band of the page you are looking at.
 */
export type PaneBand = "content" | "header";

type GutterSteps = { comfortable: number; compact: number; narrow: number };

const PANE_GUTTERS: Record<PaneBand, GutterSteps> = {
  content: { comfortable: 3, compact: 2, narrow: 1 },
  header: { comfortable: 2, compact: 2, narrow: 1 },
};

/**
 * Marks the one child of the pane that runs flush to the pane's edges instead of standing in its
 * gutter: the row a tab's content is drawn in.
 *
 * A tab is panels and tables, each of which already carries padding of its own, and the owner wants
 * them meeting the navigation drawer on one side and the comments rail on the other. The gutter is
 * for the page's chrome — its title, its tabs, its actions, the issue traverser — which is what
 * #4964 was actually about.
 *
 * Only while the pane is comfortable, though. A compact pane stops laying the tab out as panels
 * beside a table and stacks them instead — a bordered template accordion, then an issues notice,
 * then the table's own search — and stacked like that they read as a column of things needing a
 * margin, not as a surface reaching for the edges. Flush, the notice and the search sat against the
 * accordion's line and against the window. So the gutter's compact and narrow steps still apply
 * here, and only the comfortable one is given up.
 *
 * An attribute rather than a styled component, because the element that ends up being the pane's
 * direct child differs by page: the draft and active pages nest their tabs inside `Content`, the
 * archive puts `TabContent` there itself. The rule has to be about whichever of them the gutter is
 * reaching, and declaring it here keeps the pane the single place that says what its gutter does.
 */
export const PANE_FLUSH_ATTRIBUTE = "data-pane-flush";

/** Spread onto that row: `<Content {...paneFlushContent}>`. */
export const paneFlushContent = { "data-pane-flush": "" } as const;


const PROSE_INSET_UNITS = 2;

/**
 * The inset a tab's own prose stands in: its headings, its issue notices, its switches, its fields.
 *
 * A tab's row runs flush to the pane's edges so the things that are meant to meet the navigation
 * drawer and the comments rail can — the templates panel on one side, the table on the other. Text
 * and controls are not tables. Flush, a heading sits on the drawer's edge, a notice
 * and a switch sit on the resize divider, and a form's first field touches the drawer with nowhere
 * for its focus ring to go. This is the inset the pane's gutter used to give them, asked for by the
 * rows that actually want it rather than taken from the whole band.
 *
 * One number in one place on purpose. Six rows each choosing their own left padding is how the page
 * reached #4964; if this wants to change it changes here.
 */
export function workPaneProseInset(theme: Theme): CSSObject {
  return { paddingLeft: theme.spacing(PROSE_INSET_UNITS), [guttered(theme)]: { paddingLeft: 0 } };
}

/**
 * A pane that keeps its gutter on the tab's row, for the prose inset's own rule.
 *
 * The inset stands in for the gutter a comfortable pane gives up — see `PANE_FLUSH_ATTRIBUTE`. Below
 * that width the row keeps the gutter, the same 16px or 8px the header's title, tabs and actions stand
 * in, so prose inset as well sat 16px further in than the button above it. There the inset goes, and
 * everything a tab stacks — its notices, switches, headings, fields, a table's search and buttons —
 * lines up on the gutter with the header and the templates expander, and only a table's rows run past
 * it (#5907). Spelt `width <` rather than through `paneIsCompact`, which yields `max-width:` — the same
 * query, but a different object key. Callers spread this inset into styles that often carry a
 * `paneIsCompact` key of their own, and two equal keys in one object literal silently keep only the
 * later one.
 */
function guttered(theme: Theme): string {
  return `@container ${WORK_PANE_CONTAINER} (width < ${compactPaneWidth(theme)}px)`;
}

/**
 * The same inset on the trailing side as well, for prose that has been stretched to fill its row.
 *
 * The leading inset alone is right while a field keeps its own width: a Details tab on a comfortable
 * pane seats its fields in max-content columns, so nothing reaches the row's far end and there is no
 * edge to balance. A compact pane stacks those fields into a single full-width column instead, and
 * then every field ran from the leading inset to the pane's gutter — 24px from the left edge of a
 * phone and 8px from the right (#5907). Giving the inset back on the trailing side is what makes a
 * stretched field sit centred, and it is the same number so the two edges cannot drift apart.
 */
export function workPaneStretchedProseInset(theme: Theme): CSSObject {
  return {
    paddingLeft: theme.spacing(PROSE_INSET_UNITS),
    paddingRight: theme.spacing(PROSE_INSET_UNITS),
    [guttered(theme)]: { paddingLeft: 0, paddingRight: 0 },
  };
}

/**
 * The padding one gutter step puts on each side.
 *
 * A module-level function rather than one declared inside the exports below, so the computed keys
 * built from it stay out of anything the React Compiler reads — see app/CLAUDE.md -> React Compiler.
 */
function inset(theme: Theme, units: number): CSSObject {
  return { paddingLeft: theme.spacing(units), paddingRight: theme.spacing(units) };
}

/**
 * The gutter between the pane's content and the pane's edges, declared once by the pane for all of it.
 *
 * It has to be written as a rule about the pane's *children*. A container cannot query itself, so
 * the pane element gets no answer from `paneIsCompact` about its own padding — but every child of it
 * resolves `work-pane` to exactly that element, so the condition below is the pane's width even
 * though the declaration lives on the pane. The alternative was for each row to carry its own gutter,
 * which is how the page reached #4964: the header rows had 24px, the tab content had none, and their
 * compact rules dropped to zero on the understanding that the page supplied padding of its own below
 * `md` — an understanding that only held while "compact" and "mobile viewport" were the same thing.
 * Once compact became a fact about the pane, a 1280px display with comments open got the zeroed rules
 * and none of the padding, and every control sat flush against both edges.
 *
 * The page's chrome overrides this with one of its own — `workPaneHeaderGutters` — and a tab's
 * content opts out of it entirely — `PANE_FLUSH_ATTRIBUTE`. Both exceptions are declared right here,
 * so this function is still the whole answer to what the pane's gutter does. A rule per row is how
 * #4964 happened.
 */
export function workPaneGutters(theme: Theme): CSSObject {
  const steps = PANE_GUTTERS.content;
  return {
    "& > *": inset(theme, steps.comfortable),
    [paneIsCompact(theme)]: { "& > *": inset(theme, steps.compact) },
    [workPaneNarrowerThan(theme.breakpoints.values.sm)]: {
      "& > *": inset(theme, steps.narrow),
      ...narrowPaneStretchedTable(theme, steps.narrow),
    },
    // One class more specific than `& > *`, so it beats the gutter rather than tying with it — and
    // scoped to a comfortable pane, which is the only width at which running flush is right. See
    // `PANE_FLUSH_ATTRIBUTE`. The compact and narrow steps above are left to stand.
    [paneIsComfortable(theme)]: {
      [`& > [${PANE_FLUSH_ATTRIBUTE}]`]: { paddingLeft: 0, paddingRight: 0 },
    },
  };
}

/**
 * What a narrow pane does with a table marked by `PANE_STRETCH_ATTRIBUTE`.
 *
 * Every element between the tab's flush row and the table becomes a column that grows, so the table
 * takes whatever height the tab's other rows leave. `1 0 auto` rather than `1`: the table may grow into
 * spare room, never shrink below its rows — a tab taller than the pane still scrolls, as it always has.
 * The flush row itself is left alone: it is the tab's scroll container, and a row whose stretched
 * child already fills it.
 *
 * Those same elements stop clipping, because the table now hangs `gutter` past them on each side into
 * the flush row's padding — which the flush row still clips, so nothing reaches past the pane. Their
 * own heights give way too: the fee schedule's table sat in a `height: 100%` wrapper under the tab's
 * issue notice, and a hundred percent of the column plus the notice ran 71px under the traverser. The
 * table's own toolbars take the gutter back as margin, so its search and buttons stay exactly where
 * they stood and only the rows and their scrollbar run edge to edge.
 */
export function narrowPaneStretchedTable(theme: Theme, gutterSteps: number): CSSObject {
  const gutter = theme.spacing(gutterSteps);
  return {
    [`& :has([${PANE_STRETCH_ATTRIBUTE}]):not([${PANE_FLUSH_ATTRIBUTE}])`]: {
      display: "flex",
      flex: "1 0 auto",
      flexDirection: "column",
      height: "auto",
      overflow: "visible",
    },
    [`& [${PANE_STRETCH_ATTRIBUTE}]`]: {
      display: "flex",
      flex: "1 0 auto",
      flexDirection: "column",
      height: "auto",
      marginLeft: `-${gutter}`,
      marginRight: `-${gutter}`,
      // A table with a width of its own (the read-only fee schedule's is 100%) keeps that width
      // under negative margins and stops a gutter short of the right edge.
      maxWidth: "none",
      width: `calc(100% + 2 * ${gutter})`,
      "& > .MuiTableContainer-root": { flex: "1 0 auto" },
      "& > :not(.MuiTableContainer-root)": { marginLeft: gutter, marginRight: gutter },
      // MRT pads its toolbar's row by 8px, which on a phone stood the search and buttons 8px inside
      // the templates expander and the notices above them. The gutter alone is their edge.
      "& > :not(.MuiTableContainer-root) > .MuiBox-root": { paddingLeft: 0, paddingRight: 0 },
      // MRT keeps a box for its own toolbar buttons at the row's end even when a table draws none — a
      // zero-width box around an empty one — and the row's 8px gap before it still held the view
      // buttons off the right-hand edge.
      "& > :not(.MuiTableContainer-root) > .MuiBox-root > .MuiBox-root:last-child:has(> :only-child:empty)": {
        display: "none",
      },
    },
  };
}

/**
 * The gutter the page's own chrome stands in — see `PaneBand`.
 *
 * Doubled selector, because the pane declares its gutter as `& > *` and the header is one of those
 * children. `.pane > *` and `.header` carry the same specificity, so which of them won would come
 * down to the order Emotion happened to insert the two classes in; `&&` puts the header a class ahead
 * and settles it. That is also why this cannot just be padding written on the header.
 *
 * Every step lives *inside* that one selector rather than beside it, so the whole gutter is a single
 * key. Spread into a styled component, a rule keyed by `paneIsCompact` at the top level would be a
 * duplicate key in the object literal it is spread into the moment that component has a compact rule
 * of its own — and the later one silently wins. `ProposalStickyPageHeader` has exactly such a rule,
 * and the header kept its widest step at every pane width until this moved in here.
 */
export function workPaneHeaderGutters(theme: Theme): CSSObject {
  const steps = PANE_GUTTERS.header;
  return {
    "&&": {
      ...inset(theme, steps.comfortable),
      [paneIsCompact(theme)]: inset(theme, steps.compact),
      [workPaneNarrowerThan(theme.breakpoints.values.sm)]: inset(theme, steps.narrow),
    },
  };
}

/**
 * Lets one row inside a gutter reach the pane's edges anyway.
 *
 * A rule drawn across the page is a rule across the *page* — it separates one band of the page from
 * the next, so it should meet the navigation drawer on one side and the comments panel on the other,
 * the way it did when each row carried its own padding and nothing indented the row itself. Under a
 * gutter declared by the pane, a child's border box stops where the gutter starts and the rule ends
 * short at both ends.
 *
 * Negative margins of exactly the gutter, and the gutter given back as padding: the border box grows
 * out to the pane's edges while the content inside stays exactly where it was. `band` is which gutter
 * the row is climbing out of, so the rule under the actions row travels the header's inset
 * while one drawn across a tab travels the content inset.
 */
export function workPaneFullBleed(theme: Theme, band: PaneBand = "content"): CSSObject {
  const steps = PANE_GUTTERS[band];
  const bleed = (units: number): CSSObject => ({
    marginLeft: `-${theme.spacing(units)}`,
    marginRight: `-${theme.spacing(units)}`,
    paddingLeft: theme.spacing(units),
    paddingRight: theme.spacing(units),
  });
  return {
    // `width: 100%` would resolve against the parent's content box — the box the gutter already took
    // its share out of — and the negative margins would slide the row left rather than widen it.
    width: "auto",
    ...bleed(steps.comfortable),
    [paneIsCompact(theme)]: bleed(steps.compact),
    [workPaneNarrowerThan(theme.breakpoints.values.sm)]: bleed(steps.narrow),
  };
}
